import { useEffect } from 'react';
import SiteHeader from '../../Components/SiteHeader';
import { EXPERIENCES, EXPERIENCE_ORDER } from './experienceData';
import './ExperiencePage.css';

const Metric = ({ value, label }) => (
    <div className="experience-metric">
        <strong>{value}</strong>
        <span>{label}</span>
    </div>
);

const ExperiencePage = ({ experienceKey }) => {
    const experience = EXPERIENCES[experienceKey];
    const currentIndex = EXPERIENCE_ORDER.indexOf(experienceKey);
    const newer = currentIndex > 0 ? EXPERIENCES[EXPERIENCE_ORDER[currentIndex - 1]] : null;
    const older = currentIndex < EXPERIENCE_ORDER.length - 1 ? EXPERIENCES[EXPERIENCE_ORDER[currentIndex + 1]] : null;

    useEffect(() => {
        const previousTitle = document.title;
        const description = document.querySelector('meta[name="description"]');
        const previousDescription = description?.getAttribute('content');
        const canonical = document.querySelector('link[rel="canonical"]');
        const previousCanonical = canonical?.getAttribute('href');
        const title = `${experience.company} | Illya Bakurov`;
        const pageDescription = `${experience.role} at ${experience.company}. ${experience.summary}`;
        const pageUrl = `https://ibakurov.com/experience/${experience.slug}`;
        const metaUpdates = [
            ['meta[property="og:title"]', title],
            ['meta[property="og:description"]', pageDescription],
            ['meta[property="og:url"]', pageUrl],
            ['meta[name="twitter:title"]', title],
            ['meta[name="twitter:description"]', pageDescription],
        ].map(([selector, value]) => {
            const element = document.querySelector(selector);
            const previousValue = element?.getAttribute('content');
            element?.setAttribute('content', value);
            return [element, previousValue];
        });

        document.title = title;
        description?.setAttribute('content', pageDescription);
        canonical?.setAttribute('href', pageUrl);

        return () => {
            document.title = previousTitle;
            if (previousDescription) description?.setAttribute('content', previousDescription);
            if (previousCanonical) canonical?.setAttribute('href', previousCanonical);
            metaUpdates.forEach(([element, previousValue]) => {
                if (element && previousValue) element.setAttribute('content', previousValue);
            });
        };
    }, [experience]);

    return (
        <div className="experience-page">
            <SiteHeader />
            <main className="experience-detail">
                <header className="experience-hero">
                    <div className="experience-identity">
                        {experience.logo ? (
                            <img
                                src={experience.logo}
                                alt={`${experience.company} logo`}
                                className={experience.logoFit === 'contain' ? 'experience-logo--contain' : undefined}
                            />
                        ) : (
                            <span className="experience-logo-fallback" aria-hidden="true">
                                {experience.logoText || experience.company.slice(0, 2)}
                            </span>
                        )}
                        <div>
                            <p className="experience-kicker">{experience.role} · {experience.dates}</p>
                            <h1>{experience.company}</h1>
                        </div>
                    </div>
                    <h2>{experience.headline}</h2>
                    <p className="experience-summary">{experience.summary}</p>
                    {experience.domain && (
                        <a className="experience-domain" href={`https://${experience.domain}`} target="_blank" rel="noopener noreferrer">
                            {experience.domain}
                        </a>
                    )}
                </header>

                <section className={`experience-metrics experience-metrics--${experience.metrics.length}`} aria-label="Selected outcomes">
                    {experience.metrics.map((metric) => <Metric key={metric.label} {...metric} />)}
                </section>

                <section className="experience-overview">
                    {experience.overview.map((item) => (
                        <div key={item.title}>
                            <h3>{item.title}</h3>
                            <p>{item.body}</p>
                        </div>
                    ))}
                </section>

                {experience.media && (
                    <section className={`experience-media experience-media--${experience.media.length}`} aria-label="Selected work">
                        {experience.media.map((item) => (
                            <figure key={item.caption}>
                                {item.href ? (
                                    <a href={item.href} target="_blank" rel="noopener noreferrer">
                                        <img src={item.image} alt={item.alt} />
                                    </a>
                                ) : <img src={item.image} alt={item.alt} />}
                                <figcaption>
                                    {item.caption}
                                    {item.href && item.sourceLabel && (
                                        <> <a href={item.href} target="_blank" rel="noopener noreferrer">{item.sourceLabel} ↗</a></>
                                    )}
                                </figcaption>
                            </figure>
                        ))}
                    </section>
                )}

                {experience.products && experience.products.length > 0 && (
                    <section className="experience-products" aria-labelledby="experience-products-title">
                        <div className="experience-products-header">
                            <div>
                                <p className="experience-section-label">Recovered product archive</p>
                                <h2 id="experience-products-title">Sixteen products from the studio years</h2>
                            </div>
                            <p>{experience.productsIntro}</p>
                        </div>
                        <div className="experience-product-grid">
                            {experience.products.map((product) => (
                                <a
                                    className={`experience-product-card${product.featured ? ' experience-product-card--featured' : ''}`}
                                    href={product.href}
                                    key={product.name}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <div className={`experience-product-icon${product.tone ? ` experience-product-icon--${product.tone}` : ''}`}>
                                        {product.image ? (
                                            <img src={product.image} alt={`${product.name} app icon`} />
                                        ) : (
                                            <span aria-hidden="true">{product.monogram || product.name.slice(0, 2)}</span>
                                        )}
                                    </div>
                                    <p className="experience-product-category">{product.category}</p>
                                    <h3>{product.name}</h3>
                                    <p className="experience-product-description">{product.description}</p>
                                    <span className="experience-product-source">
                                        {product.sourceLabel}
                                        <span aria-hidden="true">↗</span>
                                    </span>
                                </a>
                            ))}
                            {experience.productsNote && (
                                <div className="experience-products-note">
                                    <p className="experience-section-label">Archive status</p>
                                    <p>{experience.productsNote}</p>
                                </div>
                            )}
                        </div>
                    </section>
                )}

                <section className="experience-section">
                    <p className="experience-section-label">Selected work</p>
                    <div className="experience-work-grid">
                        {experience.sections.map((section) => (
                            <article key={section.number}>
                                <span>{section.number}</span>
                                <h3>{section.title}</h3>
                                <p>{section.body}</p>
                            </article>
                        ))}
                    </div>
                </section>

                <section className="experience-stack">
                    <div>
                        <p className="experience-section-label">Tools and systems</p>
                        <h2>The stack in context</h2>
                    </div>
                    <ul>
                        {experience.stack.map((item) => <li key={item}>{item}</li>)}
                    </ul>
                </section>

                {experience.note && <p className="experience-boundary">{experience.note}</p>}

                {experience.links && experience.links.length > 0 && (
                    <section className="experience-links" aria-labelledby="experience-links-title">
                        <div>
                            <p className="experience-section-label">Public artifacts</p>
                            <h2 id="experience-links-title">Explore the work</h2>
                        </div>
                        <ul>
                            {experience.links.map((link) => (
                                <li key={link.url}>
                                    <a href={link.url} target="_blank" rel="noopener noreferrer">
                                        <span>
                                            <strong>{link.label}</strong>
                                            {link.description && <small>{link.description}</small>}
                                        </span>
                                        <span aria-hidden="true">↗</span>
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </section>
                )}

                <nav className="experience-pagination" aria-label="Experience pages">
                    {newer ? (
                        <a href={`/experience/${newer.slug}`}>
                            <span>Next</span>
                            {newer.company}
                        </a>
                    ) : <span />}
                    {older ? (
                        <a href={`/experience/${older.slug}`} className="experience-pagination-previous">
                            <span>Previous</span>
                            {older.company}
                        </a>
                    ) : <a href="/resume" className="experience-pagination-previous"><span>Résumé</span>Full résumé</a>}
                </nav>
            </main>
        </div>
    );
};

export default ExperiencePage;
